import type { GameState, Move, PieceState, Square } from '@ccc/shared-types';
import { fileOf, offsetSquare, promotionRankFor, rankOf, relativeForward, sq, startRankFor } from './board.js';
import { pieceAt } from './state.js';

type Offset = [df: number, dr: number];

const KNIGHT_OFFSETS: Offset[] = [
  [1, 2],
  [2, 1],
  [2, -1],
  [1, -2],
  [-1, -2],
  [-2, -1],
  [-2, 1],
  [-1, 2],
];

const KING_OFFSETS: Offset[] = [
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
  [0, -1],
  [1, -1],
];

const ROOK_RAYS: Offset[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

const BISHOP_RAYS: Offset[] = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

const QUEEN_RAYS: Offset[] = [...ROOK_RAYS, ...BISHOP_RAYS];

export interface MoveGenContext {
  /** Cards may add to or filter this generated list. */
  pseudoLegal: Move[];
}

function makeMove(
  piece: PieceState,
  to: Square,
  state: GameState,
  extra: Partial<Move> = {},
): Move {
  const captured = pieceAt(state, to);
  const m: Move = {
    from: piece.square,
    to,
    pieceId: piece.id,
    ...extra,
  };
  if (captured && captured.color !== piece.color) m.capturedPieceId = captured.id;
  return m;
}

function isEmpty(state: GameState, square: Square): boolean {
  return pieceAt(state, square) === undefined;
}

function isEnemy(state: GameState, square: Square, color: PieceState['color']): boolean {
  const p = pieceAt(state, square);
  return !!p && p.color !== color;
}

function isFriend(state: GameState, square: Square, color: PieceState['color']): boolean {
  const p = pieceAt(state, square);
  return !!p && p.color === color;
}

/* --- per-piece pseudo-legal move generation --- */

export function generatePawnMoves(piece: PieceState, state: GameState): Move[] {
  const moves: Move[] = [];
  const fwd = relativeForward(piece.color);
  const oneAhead = offsetSquare(piece.square, 0, fwd);
  if (oneAhead && isEmpty(state, oneAhead)) {
    pushPawnPushes(moves, piece, oneAhead);
    // double push from starting rank
    if (rankOf(piece.square) === startRankFor(piece.color, 'pawn')) {
      const twoAhead = offsetSquare(piece.square, 0, fwd * 2);
      if (twoAhead && isEmpty(state, twoAhead)) {
        moves.push(makeMove(piece, twoAhead, state));
      }
    }
  }
  // diagonal captures
  for (const df of [-1, 1] as const) {
    const t = offsetSquare(piece.square, df, fwd);
    if (t && isEnemy(state, t, piece.color)) {
      pushPawnPushes(moves, piece, t, state);
    }
  }
  // en passant
  const last = lastMoveAction(state);
  if (last) {
    const lastPiece = state.pieces[last.pieceId];
    if (
      lastPiece &&
      lastPiece.type === 'pawn' &&
      lastPiece.color !== piece.color &&
      Math.abs(rankOf(last.from) - rankOf(last.to)) === 2 &&
      rankOf(piece.square) === rankOf(last.to) &&
      Math.abs(
        fileOf(piece.square).charCodeAt(0) - fileOf(last.to).charCodeAt(0),
      ) === 1
    ) {
      const target = sq(fileOf(last.to), (rankOf(last.to) + fwd) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8);
      moves.push(makeMove(piece, target, state, { special: 'en_passant', capturedPieceId: lastPiece.id }));
    }
  }
  return moves;
}

function lastMoveAction(state: GameState): Move | undefined {
  const lastAction = state.actionHistory?.[state.actionHistory.length - 1];
  if (!lastAction) return state.history[state.history.length - 1];
  return lastAction.kind === 'move' ? lastAction.move : undefined;
}

function pushPawnPushes(out: Move[], piece: PieceState, to: Square, state?: GameState): void {
  if (rankOf(to) === promotionRankFor(piece.color)) {
    for (const promotionTo of ['queen', 'rook', 'bishop', 'knight'] as const) {
      const m: Move = { from: piece.square, to, pieceId: piece.id, promotionTo };
      if (state) {
        const cap = pieceAt(state, to);
        if (cap && cap.color !== piece.color) m.capturedPieceId = cap.id;
      }
      out.push(m);
    }
  } else {
    const m: Move = { from: piece.square, to, pieceId: piece.id };
    if (state) {
      const cap = pieceAt(state, to);
      if (cap && cap.color !== piece.color) m.capturedPieceId = cap.id;
    }
    out.push(m);
  }
}

export function generateKnightMoves(piece: PieceState, state: GameState): Move[] {
  return offsetsToMoves(piece, state, KNIGHT_OFFSETS);
}

export function generateKingMoves(piece: PieceState, state: GameState): Move[] {
  const moves = offsetsToMoves(piece, state, KING_OFFSETS);
  if (!piece.hasMoved) {
    const rank = rankOf(piece.square);
    // kingside
    const kRookSq = sq('h', rank);
    const kRook = pieceAt(state, kRookSq);
    if (
      kRook &&
      kRook.color === piece.color &&
      kRook.type === 'rook' &&
      !kRook.hasMoved &&
      isEmpty(state, sq('f', rank)) &&
      isEmpty(state, sq('g', rank))
    ) {
      moves.push({
        from: piece.square,
        to: sq('g', rank),
        pieceId: piece.id,
        special: 'castle_kingside',
      });
    }
    // queenside
    const qRookSq = sq('a', rank);
    const qRook = pieceAt(state, qRookSq);
    if (
      qRook &&
      qRook.color === piece.color &&
      qRook.type === 'rook' &&
      !qRook.hasMoved &&
      isEmpty(state, sq('b', rank)) &&
      isEmpty(state, sq('c', rank)) &&
      isEmpty(state, sq('d', rank))
    ) {
      moves.push({
        from: piece.square,
        to: sq('c', rank),
        pieceId: piece.id,
        special: 'castle_queenside',
      });
    }
  }
  return moves;
}

export function generateRookMoves(piece: PieceState, state: GameState): Move[] {
  return raysToMoves(piece, state, ROOK_RAYS);
}

export function generateBishopMoves(piece: PieceState, state: GameState): Move[] {
  return raysToMoves(piece, state, BISHOP_RAYS);
}

export function generateQueenMoves(piece: PieceState, state: GameState): Move[] {
  return raysToMoves(piece, state, QUEEN_RAYS);
}

function offsetsToMoves(piece: PieceState, state: GameState, offsets: Offset[]): Move[] {
  const out: Move[] = [];
  for (const [df, dr] of offsets) {
    const t = offsetSquare(piece.square, df, dr);
    if (!t) continue;
    if (isFriend(state, t, piece.color)) continue;
    out.push(makeMove(piece, t, state));
  }
  return out;
}

function raysToMoves(piece: PieceState, state: GameState, rays: Offset[]): Move[] {
  const out: Move[] = [];
  for (const [df, dr] of rays) {
    let f = df;
    let r = dr;
    while (true) {
      const t = offsetSquare(piece.square, f, r);
      if (!t) break;
      const occupant = pieceAt(state, t);
      if (!occupant) {
        out.push(makeMove(piece, t, state));
      } else if (occupant.color !== piece.color) {
        out.push(makeMove(piece, t, state));
        break;
      } else {
        break;
      }
      f += df;
      r += dr;
    }
  }
  return out;
}

export function generatePseudoLegalForPiece(piece: PieceState, state: GameState): Move[] {
  switch (piece.type) {
    case 'pawn':
      return generatePawnMoves(piece, state);
    case 'knight':
      return generateKnightMoves(piece, state);
    case 'bishop':
      return generateBishopMoves(piece, state);
    case 'rook':
      return generateRookMoves(piece, state);
    case 'queen':
      return generateQueenMoves(piece, state);
    case 'king':
      return generateKingMoves(piece, state);
    case 'mini-rook':
      // Mini-rooks (from the 'Mini-Rooks' card) move 1 square orthogonally.
      return offsetsToMoves(piece, state, [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]);
  }
}
